import { message } from 'antd';
import links from 'helpers/links';
import translate from 'languageProvider/inline';
import Nprogress from 'nprogress';
import { push, replace } from 'redux-first-history';
import {
  all,
  call,
  cancelled,
  delay,
  put,
  select,
  spawn,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import analyticsActions from 'redux/analytics/actions';
import {
  fetchTeamGraphql,
  mutateInviteTeamMemberGraphql,
  mutateResendTeamInviteGraphql,
  mutateRevokeTeamInviteGraphql,
  mutateEmployerAccessGraphql,
  mutateGrantAccessGraphql,
  mutateRedeemAccessGraphql,
  mutateRevokeAccessGraphql,
} from 'redux/team/graphql';
import actions from './actions';
import checkAccesRequest from './requests';

/**
 * Fetch team
 * @param {*} action
 */
export function* fetchTeamGenerator() {
  try {
    const response = yield call(fetchTeamGraphql, {
      recache: true,
    });
    // errorPolicy 'all' lets a failed companyInvites field through with the rest of the team;
    // without a team at all it is still an error.
    if (!response.data?.team) {
      throw new Error(response.errors?.[0]?.message || 'Team could not be loaded');
    }
    yield put({
      type: actions.FETCH_TEAM_DONE,
      payload: response.data.team,
    });
  } catch (e) {
    log(e);
    yield put({ type: actions.FETCH_TEAM_ERROR });
  } finally {
    if (yield cancelled()) {
      yield put({
        type: actions.FETCH_TEAM_CANCELLED,
      });
    }
  }
}

function* mutateRevokeGenerator(action) {
  try {
    yield call(() => {
      Nprogress.start();
    });
    const response = yield call(mutateRevokeAccessGraphql, {
      id: action.payload.id,
    });
    yield put({
      type: actions.MUTATE_REVOKE_DONE,
      payload: response.data.revokeProjectAccess,
    });
  } catch (e) {
    log(e);
    yield put({ type: actions.MUTATE_REVOKE_ERROR });
  } finally {
    yield call(() => {
      Nprogress.done();
    });
    if (yield cancelled()) {
      yield put({
        type: actions.MUTATE_REVOKE_CANCELLED,
      });
    }
  }
}

function* mutateGrantGenerator(action) {
  try {
    yield call(() => {
      Nprogress.start();
    });
    const response = yield call(mutateGrantAccessGraphql, action.payload);
    yield put({
      type: actions.MUTATE_GRANT_DONE,
      payload: response.data.addProjectAccess,
    });

    message.success(translate('sharePopup.success'));
  } catch (e) {
    log(e);

    if (e.message === 'GraphQL error: duplicate.entry') {
      message.error(translate('sharePopup.emailInput.duplicateEntry'));
    }

    yield put({ type: actions.MUTATE_GRANT_ERROR });
  } finally {
    yield call(() => {
      Nprogress.done();
    });
    if (yield cancelled()) {
      yield put({
        type: actions.MUTATE_GRANT_CANCELLED,
      });
    }
  }
}

function* mutateEmployerAccessGenerator(action) {
  try {
    yield call(() => {
      Nprogress.start();
    });
    const response = yield call(mutateEmployerAccessGraphql, action.payload);
    yield put({
      type: actions.MUTATE_EMPLOYER_ACCESS_DONE,
      payload: response.data.modifyEmployerAccess,
    });
    yield call(() => {
      message.success(translate('team.message.success'));
    });
  } catch (e) {
    log(e);
    yield put({ type: actions.MUTATE_EMPLOYER_ACCESS_ERROR });
  } finally {
    yield call(() => {
      Nprogress.done();
    });
    if (yield cancelled()) {
      yield put({
        type: actions.MUTATE_EMPLOYER_ACCESS_CANCELLED,
      });
    }
  }
}

const INVITE_ERRORS = [
  'companyInvite.notAdmin',
  'companyInvite.invalidEmail',
  'companyInvite.rateLimited',
  'companyInvite.notFound',
  'companyInvite.failed',
];

/* The gateway answers with one of the companyInvite.* keys; anything else is a generic failure. */
export const inviteErrorKey = (error) => {
  const key = error?.graphQLErrors?.[0]?.message;
  return INVITE_ERRORS.includes(key) ? key : 'companyInvite.failed';
};

const trackInviteEvent = (action) => analyticsActions.reduxTrackEvent('timski_rad', action);

export function* inviteMemberGenerator(action) {
  try {
    const response = yield call(mutateInviteTeamMemberGraphql, action.payload);
    yield put({ type: actions.INVITE_MEMBER_DONE, payload: response.data.inviteTeamMember });
    yield put(trackInviteEvent('poziv_poslat'));
    // The same message whatever the address — whether it already has an account is not ours to say.
    message.success(translate('team.invite.sent').replace('{email}', action.payload.email));
  } catch (e) {
    log(e);
    yield put({ type: actions.INVITE_MEMBER_ERROR });
    message.error(translate(inviteErrorKey(e)));
  }
}

export function* resendInviteGenerator(action) {
  try {
    const response = yield call(mutateResendTeamInviteGraphql, action.payload);
    const invite = response.data.resendTeamInvite;
    yield put({ type: actions.RESEND_INVITE_DONE, payload: invite });
    yield put(trackInviteEvent('poziv_ponovo_poslat'));
    message.success(translate('team.invite.resent').replace('{email}', invite.email));
  } catch (e) {
    log(e);
    yield put({ type: actions.RESEND_INVITE_ERROR });
    message.error(translate(inviteErrorKey(e)));
  }
}

export function* revokeInviteGenerator(action) {
  try {
    const response = yield call(mutateRevokeTeamInviteGraphql, action.payload);
    yield put({ type: actions.REVOKE_INVITE_DONE, payload: response.data.revokeTeamInvite });
    yield put(trackInviteEvent('poziv_opozvan'));
    message.success(translate('team.invite.revoked'));
  } catch (e) {
    log(e);
    yield put({ type: actions.REVOKE_INVITE_ERROR });
    message.error(translate(inviteErrorKey(e)));
  }
}

/* redeem access generator */
export function* redeemAccessGenerator(action) {
  try {
    let userExists = false;
    try {
      const userExistsResponse = yield call(checkAccesRequest, action.payload);
      userExists = userExistsResponse.status === 200;
    } catch (e) {
      log(e);
    }

    /**
     * Special case!
     * we check if the user exists and then redirect where neccessary
     */

    const store = yield select((state) => state.Auth);
    const isLoggedIn = store.get('loggedIn');

    if (!isLoggedIn) {
      const {
        location: { pathname, search },
      } = window;
      window.sessionStorage.setItem('redirectTo', `${pathname}${search}`);
      window.location.href = userExists
        ? links.oauth().authorize
        : links.oauth({
            redirectUri: window.btoa(window.location.href),
          }).register;
      return;
    }

    const response = yield call(mutateRedeemAccessGraphql, action.payload);
    yield put({
      type: actions.REDEEM_ACCESS_DONE,
      payload: response.data,
    });
    message.success(translate('invite.success'));
    yield put(push(links.project(response.data.redeemAccess.allowedContent).url));
  } catch (e) {
    const { message: errorMessage } = e;
    let payload = false;
    if (errorMessage.indexOf('Unable to find access') !== -1) {
      payload = 'invite.missing_token';
    }
    if (errorMessage.indexOf('access_denied') !== -1) {
      payload = 'invite.access_denied';
    }
    yield put({ type: actions.REDEEM_ACCESS_ERROR, payload });
  } finally {
    if (yield cancelled()) {
      yield put({ type: actions.REDEEM_ACCESS_CANCELLED });
    }
  }
}

export function* redeemAccessErrorMessageGenerator(action) {
  if (action.payload) {
    message.error(translate(action.payload));
    yield delay(3000);
    yield put(replace(links.dashboard.url));
  }
}

/* fetch Team saga */
export function* fetchTeam() {
  yield takeEvery([actions.FETCH_TEAM_START], fetchTeamGenerator);
}

/* remove access */
export function* mutateRevoke() {
  yield takeEvery([actions.MUTATE_REVOKE_START], mutateRevokeGenerator);
}

/* grant access */
export function* mutateGrant() {
  yield takeEvery([actions.MUTATE_GRANT_START], mutateGrantGenerator);
}

/* change employer access */
export function* mutateEmployerAccess() {
  yield takeEvery([actions.MUTATE_EMPLOYER_ACCESS_START], mutateEmployerAccessGenerator);
}

/* company invites */
export function* inviteMember() {
  yield takeLatest(actions.INVITE_MEMBER_START, inviteMemberGenerator);
}

export function* resendInvite() {
  yield takeEvery(actions.RESEND_INVITE_START, resendInviteGenerator);
}

export function* revokeInvite() {
  yield takeEvery(actions.REVOKE_INVITE_START, revokeInviteGenerator);
}

/* redeem access */
export function* redeemAccess() {
  yield takeEvery(actions.REDEEM_ACCESS_START, redeemAccessGenerator);
}

function* redeemError() {
  yield takeLatest(actions.REDEEM_ACCESS_ERROR, redeemAccessErrorMessageGenerator);
}

export default function* rootSaga() {
  yield all([
    spawn(fetchTeam),
    spawn(mutateRevoke),
    spawn(mutateGrant),
    spawn(mutateEmployerAccess),
    spawn(redeemAccess),
    spawn(redeemError),
    spawn(inviteMember),
    spawn(resendInvite),
    spawn(revokeInvite),
  ]);
}
